import React, { Component, Fragment, lazy } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { when } from 'mobx';
import { NavLink, Redirect, Switch } from 'react-router-dom';
import ProfileIcon from '@material-ui/icons/AccountBox';
import WallIcon from '@material-ui/icons/Chat';
import CollectionsIcon from '@material-ui/icons/Collections';
import GiftIcon from '@material-ui/icons/Redeem';
import StarsIcon from '@material-ui/icons/Stars';
import StoreIcon from '@material-ui/icons/Store';

import { translate } from 'services/i18nService';
import trackingService from 'services/UITrackingService';
import FanClubStore from 'stores/FanClubStore';
import FirebaseStore from 'stores/FirebaseStore';
import GroupShowStore from 'stores/GroupShowStore';
import MyShopStore from 'stores/MyShopStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import EmbedCamOffline from 'components/EmbedCamOffline/EmbedCamOffline';
import WaitingComponent from 'components/WaitingComponent/WaitingComponent';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import { VIDEO_AND_CHAT_CLASS } from 'enum/videoPlayer';
import TrackedRoute from 'overwrites/TrackedRoute';
import {
  isMyProfileHelperFunc,
  generateID,
} from 'utils/helpers';

import GalleryStore from '../ProfileV2Mobile/MobileGallery/GalleryStore';

import styles from './ProfileV2Desktop.css';
import LegalArea from './LegalArea/LegalArea';
import ProfileBanner from './ProfileBanner/ProfileBanner';
import ChatWallStore from './ChatWall/ChatWallStore';


const Profile = lazy(() => import('./Profile/Profile'));
const FanClub = lazy(() => import('../ProfileV2Mobile/FanClubV2/FanClubV2'));
const MyShop = lazy(() => import('./MyShop/MyShop'));
const GiftPage = lazy(() => import('./Gifts/GiftPage'));
const Gallery = lazy(() => import('./Gallery/Gallery'));
const ChatWall = lazy(() => import('./ChatWall/ChatWall'));
const DesktopBroadcast = lazy(() => import('./Broadcast/DesktopBroadcast/DesktopBroadcast'));

@observer
class ProfileV2Desktop extends Component {
  static propTypes = {
    appActions: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    paymentActions: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    walletDetails: PropTypes.object.isRequired,
    directoryActions: PropTypes.object,
    embedProfileV2: PropTypes.bool,
    history: PropTypes.object,
  };

  constructor(...args) {
    super(...args);

    this.IDS = {
      prefix: '75r8Q4vR',
      element: 'ProfileV2Desktop',
      navigation: 'navigation',
    };
    this.makeId = generateID(this.IDS.prefix, this.IDS.element);

    this.navRef = React.createRef();
  }

  componentDidMount() {
    const {
      user: { username },
      match,
    } = this.props;
    const performerName = match.params.user;
    if (!ProfileStore.performerName) {
      ProfileStore.setProfileName(performerName);
    }
    when(
      () => ProfileStore.profileExists,
      () => this.initialSetup(username, performerName)
    );
  }

  componentDidUpdate(prevProps) {
    const {
      user: { username },
      match,
    } = this.props;
    const performerName = match.params.user;
    if (prevProps.user.username !== username && username) {
      ProfileStore.getPerformerFavouritesStatus(username);
      ProfileStore.getPerformerFriendsStatus(username);
      if (username.toLowerCase() === performerName) {
        ProfileStore.getPerformerFriends(username);
      }
    }

    if (prevProps.user.username !== username) {
      ChatWallStore.getComments(performerName);
    }
  }

  initialSetup = (username, performerName) => {
    ProfileStore.getPerformerFavouritesCount();
    ChatWallStore.getComments(performerName);
    if (username) {
      ProfileStore.getPerformerFavouritesStatus(username);
      ProfileStore.getPerformerFriendsStatus(username);
      if (username.toLowerCase() === performerName) {
        ProfileStore.getPerformerFriends(username);
      }
    }
  };

  scrollToContent = () => {
    const bannerHeight = 76;
    const isMyProfile = isMyProfileHelperFunc(this.props.user, ProfileStore.performerName);
    if (ProfileStore.performerOnline && !isMyProfile && window.pageYOffset === 0) {
      const videoHeight = document.querySelector(`.${VIDEO_AND_CHAT_CLASS}`);
      if (videoHeight) {
        window.scrollTo({ top: videoHeight.clientHeight + bannerHeight, behavior: 'smooth' });
      }
    }
  };

  navigationClick = (tab) => {
    const { props } = this;
    const { broadcastStatus } = ProfileStore;

    return (...args) => {
      const payload = {
        tabTitle: tab.title,
        broadcastStatus,
        broadcaster: props.match.params.user,
      };
      trackingService.emit('broadcasterPageTabClick', payload);

      this.scrollToContent(...args);
    };
  };

  getIsActive = (tab) => (_, { pathname }) => {
    if (pathname.includes('myshop')) {
      return tab.route.includes('myshop');
    }

    return [tab.route, tab.additionalRoute].includes(pathname);
  };

  renderTabs = () => {
    const canAccessGifts = UserStore.hasCF(allUserGroups.GIFTS_REVAMP);
    const { match: { params: { user } } } = this.props;
    const tabs = [
      {
        id: 'profileIcon',
        icon: <ProfileIcon />,
        title: translate('v2profile.profile.title'),
        route: routeDefinitions.get(routeDefinitions.profileV2.profile, { user }),
        additionalRoute: routeDefinitions.get(routeDefinitions.profileV2.root, { user }),
      },
      {
        id: 'starsIcon',
        icon: <StarsIcon />,
        title: translate('v2profile.fanclub.title'),
        route: routeDefinitions.get(routeDefinitions.profileV2.fanclub, { user }),
      },
      {
        id: 'giftIcon',
        icon: <GiftIcon />,
        title: translate('v2profile.gifts.title'),
        route: canAccessGifts ? routeDefinitions.get(routeDefinitions.profileV2.gifts, { user }) : '#',
      },
      {
        id: 'storeIcon',
        icon: <StoreIcon />,
        title: translate('v2profile.myshop.title'),
        route: routeDefinitions.get(routeDefinitions.profileV2.myShop, { user }),
      },
      {
        id: 'collectionsIcon',
        icon: <CollectionsIcon />,
        title: translate('v2profile.gallery.title'),
        route: routeDefinitions.get(routeDefinitions.profileV2.gallery, { user }),
      },
    ];

    const chatWallTab = {
      icon: <WallIcon />,
      title: translate('v2profile.chatwall.title'),
      route: routeDefinitions.get(routeDefinitions.profileV2.wall, { user }),
    };

    if (ChatWallStore.isCommentsPageVisible) {
      tabs.push(chatWallTab);
    }
    if (!FanClubStore.performerFanClubInfo.activated) {
      tabs.splice(
        tabs.findIndex((tab) => tab.id === 'starsIcon'),
        1
      );
    }
    if (!MyShopStore.performerVideos.length) {
      tabs.splice(
        tabs.findIndex((tab) => tab.id === 'storeIcon'),
        1
      );
    }
    if (!GalleryStore.hasPhotos) {
      tabs.splice(
        tabs.findIndex((tab) => tab.id === 'collectionsIcon'),
        1
      );
    }

    return tabs;
  };

  renderChatWallWaitingComp = () => (WaitingComponent(ChatWall)({ ...this.props }));

  renderGalleryWaitingComp = (props) => (WaitingComponent(Gallery)({ ...props, ...this.props }));

  renderGitsWaitingComp = (props) => {
    const { paymentActions: { showBuyTokensModal } } = this.props;

    return (WaitingComponent(GiftPage)({
      ...props,
      showBuyTokensModal, isDesktop: true, navRef: this.navRef,
    }));
  };

  renderGiftWaitingComp = (props) => {
    const { paymentActions: { showBuyTokensModal } } = this.props;

    return (WaitingComponent(GiftPage)({
      ...props,
      showBuyTokensModal, isDesktop: true, navRef: this.navRef,
    }));
  };

  renderMyShopWaitingComp = (props) => {
    const {
      paymentActions: { showBuyTokensModal },
      match: { params: { user: performerName } },
      appActions: { setShowRegistrationModal },
      user,
    } = this.props;

    return (
      WaitingComponent(MyShop)({
        ...props,
        user,
        performerName,
        setShowRegistrationModal,
        showBuyTokensModal,
      })
    );
  };

  renderFunClubWaitingComp = (props) => (
    WaitingComponent(FanClub)({ ...props, ...this.props, isDesktop: true })
  );

  renderEmbedProfileWaitingComp = (props) => {
    const { embedProfileV2 } = this.props;

    return (WaitingComponent(Profile)({ ...props, ...this.props, embedProfileV2 }));
  };

  render() {
    const {
      match: { params: { user: performerName } },
      user,
      walletDetails,
      appActions,
      paymentActions: { showBuyTokensModal },
      embedProfileV2,
      directoryActions,
    } = this.props;
    const { performerOnline, broadcastStatus } = ProfileStore;
    const { disconnectTimestamp, newShowsDetails, newShowsState, roomDataReady, authData } = FirebaseStore;
    const { userHaveTicket } = GroupShowStore;
    const { setShowRegistrationModal } = appActions;

    const isMyProfile = isMyProfileHelperFunc(user, performerName);
    const playerBroadcastMode = UserStore.hasCF(allUserGroups.BROADCASTER_AS_VIEWER);

    return (
      <Fragment>
        {!embedProfileV2 && (
          <ProfileBanner
            history={this.props.history}
            setIsAnyModalShown={directoryActions?.setIsAnyModalShown}
            setShowRegistrationModal={setShowRegistrationModal}
            user={user}
          />
        )}
        {performerOnline && (!isMyProfile || playerBroadcastMode) && (
          <DesktopBroadcast
            {...this.props}
            authDataStatus={authData.status}
            broadcastStatus={broadcastStatus}
            disconnectTimestamp={disconnectTimestamp}
            embedProfileV2={embedProfileV2}
            match={this.props.match}
            newShowsDetails={newShowsDetails}
            newShowsState={newShowsState}
            roomDataReady={roomDataReady}
            setShowRegistrationModal={setShowRegistrationModal}
            showBuyTokensModal={showBuyTokensModal}
            spyRequestInProgress={ProfileStore.spyRequestInProgress}
            user={user}
            userHoldGroupShowTicket={userHaveTicket(this.props.user.username)}
            walletDetails={walletDetails}
          />
        )}
        {!performerOnline && embedProfileV2 && <EmbedCamOffline />}
        {!embedProfileV2 && (
          <div ref={this.navRef} className={styles.nav}>
            {this.renderTabs().map((tab, index) => (
              <NavLink
                key={tab.route}
                activeClassName={styles.navItemActive}
                className={styles.navItem}
                exact={true}
                id={`${this.makeId(this.IDS.navigation)}-${index + 1}`}
                isActive={this.getIsActive(tab)}
                to={tab.route}
                onClick={this.navigationClick(tab)}
              >
                {tab.icon}
                <span className={styles.navTitle}>{tab.title}</span>
              </NavLink>
            ))}
          </div>
        )}
        <div className={styles.content}>
          <Switch>
            <TrackedRoute
              exact={true}
              path={[
                routeDefinitions.profileV2.root,
                routeDefinitions.profileV2.profile,
                routeDefinitions.embedProfileV2,
              ]}
              render={this.renderEmbedProfileWaitingComp}
            />
            {!embedProfileV2 && (
              <Fragment>
                <TrackedRoute
                  exact={true}
                  path={routeDefinitions.profileV2.fanclub}
                  render={this.renderFunClubWaitingComp}
                />
                <TrackedRoute
                  path={routeDefinitions.profileV2.myShop}
                  render={this.renderMyShopWaitingComp}
                />
                <TrackedRoute
                  exact={true}
                  path={routeDefinitions.profileV2.gifts}
                  render={this.renderGiftWaitingComp}
                />
                <TrackedRoute
                  exact={true}
                  path={routeDefinitions.profileV2.gallery}
                  render={this.renderGalleryWaitingComp}
                />
                {ChatWallStore.isCommentsPageVisible && (
                  <TrackedRoute
                    exact={true}
                    path={routeDefinitions.profileV2.wall}
                    render={this.renderChatWallWaitingComp}
                  />
                )}
              </Fragment>
            )}
            <Redirect to={routeDefinitions.pageNotFound} />
          </Switch>
        </div>
        {!embedProfileV2 && (
          <Fragment>
            <LegalArea />
          </Fragment>
        )}
      </Fragment>
    );
  }
}

export default observer(ProfileV2Desktop);
